import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowRight, Loader2, Shield } from 'lucide-react';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role !== 'admin') {
        toast.error('Admin access required');
        return;
      }
      toast.success('Welcome back, Admin!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050507] flex items-center justify-center p-4 sm:p-8 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-violet-500/50 to-transparent"></div>
        <div className="absolute top-1/3 -left-20 w-64 h-64 bg-violet-600 rounded-full filter blur-[150px] opacity-15"></div>
        <div className="absolute bottom-1/3 -right-20 w-64 h-64 bg-cyan-600 rounded-full filter blur-[150px] opacity-15"></div>
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>
      
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-xl mb-4 sm:mb-6 shadow-lg glow-violet">
            <Shield className="text-white" size={28} />
          </div>
          <Link to="/">
            <Logo variant="light" />
          </Link>
          <p className="text-slate-500 mt-2 text-sm sm:text-base">Administrator Portal</p>
        </div>

        <div className="bg-[#12121A]/80 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-slate-800 shadow-2xl animate-fade-in-up">
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
            Admin Sign In
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-400">Username</Label>
              <Input
                id="email"
                type="text"
                placeholder="mightylink"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 sm:h-12 bg-[#0A0A0F] border-slate-700 text-white placeholder:text-slate-600 focus:border-violet-500 focus:ring-violet-500/20"
                data-testid="admin-login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-400">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 sm:h-12 bg-[#0A0A0F] border-slate-700 text-white placeholder:text-slate-600 pr-12 focus:border-violet-500 focus:ring-violet-500/20"
                  data-testid="admin-login-password"
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 sm:h-12 btn-gradient text-white border-0"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2" size={18} />
                </>
              )}
            </Button>
          </form>

          <div className="mt-4 sm:mt-6 text-center">
            <Link to="/login" className="text-slate-500 hover:text-violet-400 text-sm transition-colors">
              Back to User Login
            </Link>
          </div>
        </div>

        <p className="text-center text-slate-600 text-xs sm:text-sm mt-4 sm:mt-6">
          Need admin access? Contact your system administrator.
        </p>
      </div>
    </div>
  );
}
